// src/index.js
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import { BrowserRouter } from 'react-router-dom';
import 'semantic-ui-css/semantic.min.css';
import ScrollToTop from './app/common/util/ScrollToTop';
import App from './app/layout/App';
import { isChunkLoadError } from './app/layout/ErrorBoundary';
import './app/scss/main.scss';
import { configureStore } from './app/store/configureStore';
import * as serviceWorker from './serviceWorkerRegistration';

import { ReactReduxFirebaseProvider } from 'react-redux-firebase';
import { createFirestoreInstance } from 'redux-firestore';
import { logAppError } from './app/common/util/errorLogger';
import { versionDate } from './app/common/util/helpers';
import firebase from './app/config/firebase';
import InstallPrompt from './pwa/InstallPrompt';

// NOTE: Firestore settings (cacheSizeBytes, ignoreUndefinedProperties) are
// configured once in src/app/config/firebase.js before persistence is enabled.
// Calling db.settings() here ran after the SDK had already started, so it had
// no effect and emitted the "overriding the original host" warning.

// Suppress React 18 warnings for third-party libraries
const originalError = console.error;
console.error = (...args) => {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('defaultProps') || args[0].includes('findDOMNode'))
  ) {
    return;
  }
  originalError(...args);
};

// ----------------------------------------
// iOS Service Worker Update Recovery
// ----------------------------------------
function handleIOSServiceWorkerRecovery() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isUpdating = sessionStorage.getItem('sw_updating');
  const updateTime = sessionStorage.getItem('sw_update_time');

  if (isIOS && isUpdating) {
    const timeSinceUpdate = Date.now() - parseInt(updateTime || '0', 10);

    // If it's been less than 45 seconds since update, show recovery overlay
    if (timeSinceUpdate < 45000) {
      showIOSRecoveryOverlay();

      // Force re-initialization after a short delay
      setTimeout(() => {
        // Clear the update flags
        sessionStorage.removeItem('sw_updating');
        sessionStorage.removeItem('sw_update_time');

        // Remove recovery overlay
        const overlay = document.getElementById('ios-recovery-overlay');
        if (overlay) {
          overlay.remove();
        }

        // Force React to re-render by dispatching a custom event
        window.dispatchEvent(new Event('sw-recovery-complete'));
      }, 3000);
    } else {
      // Clean up old flags
      sessionStorage.removeItem('sw_updating');
      sessionStorage.removeItem('sw_update_time');
    }
  }
}

// ----------------------------------------
// Storage budget enforcement (prevents runaway CacheStorage/IndexedDB usage)
// ----------------------------------------
const CLEANUP_LOCK_KEY = 'portal_storage_cleanup_lock';
const CLEANUP_LOCK_TTL = 2 * 60 * 1000; // 2 minutes

// ----------------------------------------
// Multi-tab awareness
// ----------------------------------------
// Firestore persistence is shared across tabs (synchronizeTabs: true). Deleting
// the Firestore/Firebase IndexedDB databases while other tabs are open pulls the
// backing store out from under them, which manifests as stuck spinners, failed
// saves, and unexpected sign-outs. To avoid this we register a long-lived Web
// Lock per tab and only perform destructive cleanup when this is the only tab.
const TAB_LOCK_NAME = 'portal_open_tab';

try {
  if (navigator.locks && typeof navigator.locks.request === 'function') {
    // Held for the lifetime of the tab (promise never resolves).
    navigator.locks.request(
      TAB_LOCK_NAME,
      { mode: 'shared' },
      () => new Promise(() => {}),
    );
  }
} catch (_) {}

async function otherTabsOpen() {
  try {
    if (!navigator.locks || typeof navigator.locks.query !== 'function') {
      // Can't determine tab count — assume other tabs may be open and be safe.
      return true;
    }
    const state = await navigator.locks.query();
    const held = (state && state.held) || [];
    const tabCount = held.filter((l) => l && l.name === TAB_LOCK_NAME).length;
    return tabCount > 1;
  } catch (_) {
    // On any failure, err on the safe side and skip destructive cleanup.
    return true;
  }
}

function acquireCleanupLock() {
  try {
    const existing = localStorage.getItem(CLEANUP_LOCK_KEY);
    if (existing) {
      const ts = parseInt(existing, 10);
      if (!isNaN(ts) && Date.now() - ts < CLEANUP_LOCK_TTL) {
        return false;
      }
    }
    localStorage.setItem(CLEANUP_LOCK_KEY, Date.now().toString());
    return true;
  } catch (_) {
    return true;
  }
}

function releaseCleanupLock() {
  try {
    localStorage.removeItem(CLEANUP_LOCK_KEY);
  } catch (_) {}
}

async function enforceStorageBudget() {
  let haveLock = false;
  let willReload = false;

  try {
    if (
      !('storage' in navigator) ||
      typeof navigator.storage.estimate !== 'function'
    ) {
      return;
    }

    const estimate = await navigator.storage.estimate();
    const usage = Number(estimate?.usage || 0);
    const quota = Number(estimate?.quota || 0);

    const budgetMb = Number(process.env.REACT_APP_STORAGE_BUDGET_MB || 200);
    const budgetBytes = budgetMb * 1024 * 1024;
    const overAbsolute = usage > budgetBytes;
    const overRelative = quota > 0 ? usage / quota > 0.6 : false;
    if (!overAbsolute && !overRelative) return;

    if (!acquireCleanupLock()) return;
    haveLock = true;

    if ('caches' in window) {
      try {
        const names = await caches.keys();
        const runtimeCaches = names.filter(
          (name) =>
            !String(name).includes('workbox-precache') &&
            !String(name).includes('workbox-runtime'),
        );
        await Promise.all(runtimeCaches.map((n) => caches.delete(n)));
      } catch (_) {}
    }

    // Deleting the shared Firestore/Firebase IndexedDB stores while other tabs
    // are open breaks those tabs (stuck spinners, failed saves, sign-outs).
    // Clearing CacheStorage above is safe; defer the destructive IndexedDB
    // cleanup + reload until this is the only open tab.
    if (await otherTabsOpen()) {
      return;
    }

    let clearedAnyDb = false;
    if ('indexedDB' in window) {
      try {
        const shouldDeleteDb = (name) => {
          const n = String(name || '');
          if (!n) return false;
          // Never delete firebaseLocalStorageDb — it holds the auth
          // token. Removing it signs the user out on next reload.
          if (n === 'firebaseLocalStorageDb') return false;
          if (n.startsWith('firestore/')) return true;
          if (n.startsWith('firebase-')) return true;
          if (n === 'workbox-expiration') return true;
          return false;
        };

        const deleteDb = (name) =>
          new Promise((resolve) => {
            const req = indexedDB.deleteDatabase(name);
            req.onsuccess = () => resolve(true);
            req.onerror = () => resolve(false);
            req.onblocked = () => resolve(false);
          });

        if (typeof indexedDB.databases === 'function') {
          const dbs = await indexedDB.databases();
          const names = (dbs || [])
            .map((d) => d && d.name)
            .filter(Boolean)
            .filter(shouldDeleteDb);

          const results = await Promise.all(names.map((n) => deleteDb(n)));
          clearedAnyDb = results.some(Boolean);
        } else {
          const projectId =
            process.env.REACT_APP_FIREBASE_PROJECT_ID || 'reactportal-5992d';
          const knownNames = [
            // NOTE: firebaseLocalStorageDb is intentionally excluded —
            // deleting it signs the user out on reload.
            'firebase-app-check-database',
            'firebase-heartbeat-database',
            'workbox-expiration',
            `firestore/[DEFAULT]/${projectId}/main`,
          ];
          const results = await Promise.all(knownNames.map((n) => deleteDb(n)));
          clearedAnyDb = results.some(Boolean);
        }
      } catch (_) {}
    }

    // If we just cleared Firestore/Firebase persistence, a reload avoids any blocked handles
    // and ensures a clean auth/profile boot.
    if (clearedAnyDb) {
      willReload = true;
      try {
        window.location.replace(window.location.href);
      } catch (_) {}
    }
  } catch (_) {
  } finally {
    if (haveLock && !willReload) {
      releaseCleanupLock();
    }
  }
}

// Kick once on startup + periodically
try {
  setTimeout(() => {
    enforceStorageBudget();
  }, 2500);
  setInterval(
    () => {
      enforceStorageBudget();
    },
    10 * 60 * 1000,
  );
} catch (_) {}

function showIOSRecoveryOverlay() {
  // Remove any existing overlay
  const existing = document.getElementById('ios-recovery-overlay');
  if (existing) {
    existing.remove();
  }

  const overlay = document.createElement('div');
  overlay.id = 'ios-recovery-overlay';
  overlay.innerHTML = `
    <div style="
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      background: rgba(255,255,255,0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    ">
      <div style="
        text-align: center;
        padding: 30px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        max-width: 300px;
      ">
        <div style="
          width: 30px;
          height: 30px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        "></div>
        <div style="
          font-size: 16px;
          color: #333;
          margin-bottom: 8px;
        ">Finalizing update...</div>
        <div style="
          font-size: 12px;
          color: #666;
        ">App will be ready in a moment</div>
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `;
  document.body.appendChild(overlay);
}

// Show loading overlay during update
function showUpdateOverlay() {
  // Remove any existing overlay first
  const existing = document.getElementById('sw-update-overlay');
  if (existing) {
    existing.remove();
  }

  const overlay = document.createElement('div');
  overlay.id = 'sw-update-overlay';
  overlay.innerHTML = `
    <div style="
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      background: rgba(255,255,255,0.95);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    ">
      <div style="
        text-align: center;
        padding: 30px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      ">
        <div style="
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        "></div>
        <div style="
          font-size: 18px;
          color: #333;
          margin-bottom: 8px;
        ">Updating app...</div>
        <div style="
          font-size: 14px;
          color: #666;
        ">Please wait while we load the latest version</div>
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `;
  document.body.appendChild(overlay);
}

// Enhanced update handling
function handleServiceWorkerUpdate(registration) {
  if (!registration || !registration.waiting) {
    // Show overlay and force reload if no waiting worker
    showUpdateOverlay();
    setTimeout(() => performReload(), 500);
    return;
  }

  // Show the loading overlay immediately when user clicks reload
  showUpdateOverlay();

  let reloadExecuted = false;

  const performReloadOnce = () => {
    if (reloadExecuted) return;
    reloadExecuted = true;
    // Small delay to ensure overlay is visible
    setTimeout(() => performReload(), 300);
  };

  // Listen for the new SW to become active
  const handleControllerChange = () => {
    performReloadOnce();
  };

  // Listen for SW activation confirmation
  const handleMessage = (event) => {
    if (event.data && event.data.type === 'SW_ACTIVATED') {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      performReloadOnce();
    }
  };

  // Set up listeners
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    handleControllerChange,
    { once: true },
  );
  navigator.serviceWorker.addEventListener('message', handleMessage);

  // Tell the waiting SW to activate
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });

  // Safety timeout in case events don't fire
  setTimeout(() => {
    navigator.serviceWorker.removeEventListener(
      'controllerchange',
      handleControllerChange,
    );
    navigator.serviceWorker.removeEventListener('message', handleMessage);
    performReloadOnce();
  }, 5000);
}

function performReload() {
  try {
    // Mark that we're reloading for a SW update so we don't show the popup again
    sessionStorage.setItem('sw_just_updated', Date.now().toString());

    // Clear any remaining caches from the browser side as a safety measure
    if ('caches' in window) {
      caches
        .keys()
        .then((names) => {
          // Only clear runtime caches, not precache
          const runtimeCaches = names.filter(
            (name) =>
              !name.includes('workbox-precache') &&
              (name.includes('html-nav') ||
                name.includes('static-assets') ||
                name.includes('api')),
          );
          return Promise.all(runtimeCaches.map((name) => caches.delete(name)));
        })
        .catch(() => {
          // Ignore cache clearing errors
        });
    }

    // iOS-specific handling for service worker updates
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      // Store a flag to indicate we're in an update process
      sessionStorage.setItem('sw_updating', 'true');
      sessionStorage.setItem('sw_update_time', Date.now().toString());

      // Use replace instead of href to avoid adding to history
      const cleanUrl = window.location.href.split('?')[0].split('#')[0];
      window.location.replace(`${cleanUrl}?sw_update=${Date.now()}`);
    } else if ('reload' in window.location) {
      // Modern browsers - force hard reload
      window.location.reload(true);
    } else {
      // Fallback: navigate to same URL with cache-busting
      const url = new URL(window.location);
      url.searchParams.set('_sw_reload', Date.now().toString());
      window.location.href = url.toString();
    }
  } catch (error) {
    // Ultimate fallback
    window.location.href = window.location.pathname + '?_reload=' + Date.now();
  }
}

// ----------------------------------------
// Firestore IndexedDB corruption auto-recovery
// Catches errors like "Cannot read properties of null (reading 'dt')"
// commonly caused by multi-tab Firestore persistence conflicts.
// ----------------------------------------
const RECOVERY_KEY = 'firestore_recovery_attempted';

function isFirestoreCacheCorruption(error) {
  const msg = String(error?.message || error || '');
  return (
    // The specific 'dt' null reference error from corrupted cache
    (msg.includes('Cannot read properties of null') &&
      (msg.includes("'dt'") || msg.includes('"dt"'))) ||
    // Other known Firestore IndexedDB corruption patterns
    msg.includes("Failed to execute 'transaction' on 'IDBDatabase'") ||
    msg.includes(
      'An internal error was encountered in the Indexed Database server',
    ) ||
    (msg.includes('UnknownError') && msg.includes('IndexedDB'))
  );
}

// Transient network failures during Firebase auth/App Check token refresh
// (often triggered by multi-tab storage-event token syncs). These are benign —
// the SDK retries — so they shouldn't be logged as app errors or surfaced.
function isTransientAuthNetworkError(error) {
  const code = String(error?.code || '');
  const msg = String(error?.message || error || '');
  return (
    code === 'auth/network-request-failed' ||
    msg.includes('auth/network-request-failed') ||
    code === 'appCheck/fetch-status-error' ||
    msg.includes('appCheck/fetch-status-error')
  );
}

async function recoverFromCorruption() {
  // Prevent infinite reload loops — only attempt once per session
  if (sessionStorage.getItem(RECOVERY_KEY)) return;
  sessionStorage.setItem(RECOVERY_KEY, Date.now().toString());

  console.warn(
    '[Recovery] Firestore cache corruption detected, clearing persistence and reloading...',
  );

  if ('indexedDB' in window) {
    const projectId =
      process.env.REACT_APP_FIREBASE_PROJECT_ID || 'reactportal-5992d';
    const dbNames = [
      `firestore/[DEFAULT]/${projectId}/main`,
      'firebase-heartbeat-database',
    ];
    const deleteDb = (name) =>
      new Promise((resolve) => {
        try {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = () => resolve(true);
          req.onerror = () => resolve(false);
          req.onblocked = () => resolve(false);
        } catch (_) {
          resolve(false);
        }
      });
    await Promise.all(dbNames.map(deleteDb));
  }

  window.location.reload();
}

// Clear the recovery flag on successful load so future corruptions can be caught
window.addEventListener('load', () => {
  setTimeout(() => {
    sessionStorage.removeItem(RECOVERY_KEY);
  }, 5000);
});

// Recover from a stale-deploy chunk load failure by reloading once to fetch
// fresh assets. Shares the loop guard key with the ErrorBoundary.
const CHUNK_RELOAD_KEY = 'chunk_reload_attempt';
function recoverFromChunkError() {
  let lastAttempt = 0;
  try {
    lastAttempt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch (_) {}
  if (Date.now() - lastAttempt <= 20000) return; // recently tried; avoid loop
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch (_) {}
  window.location.reload();
}

window.addEventListener('error', (event) => {
  if (isFirestoreCacheCorruption(event.error)) {
    event.preventDefault();
    recoverFromCorruption();
    return;
  }
  if (isChunkLoadError(event.error || event.message)) {
    event.preventDefault();
    recoverFromChunkError();
    return;
  }
  if (isTransientAuthNetworkError(event.error || event.message)) {
    // Benign network blip during token refresh — don't log or surface.
    event.preventDefault();
    return;
  }
  // Diagnostic logging for unexpected crashes
  logAppError({
    error: event.error || event.message,
    source: 'window.onerror',
  });
  console.error(
    '[Portal] Uncaught error on',
    window.location.pathname,
    '—',
    event.message,
    event.error,
  );
});

window.addEventListener('unhandledrejection', (event) => {
  if (isFirestoreCacheCorruption(event.reason)) {
    event.preventDefault();
    recoverFromCorruption();
    return;
  }
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    recoverFromChunkError();
    return;
  }
  if (isTransientAuthNetworkError(event.reason)) {
    // Benign network blip during token refresh — don't log or surface.
    event.preventDefault();
    return;
  }
  // Diagnostic logging for unhandled promise rejections
  logAppError({
    error: event.reason,
    source: 'unhandledrejection',
  });
  console.error(
    '[Portal] Unhandled rejection on',
    window.location.pathname,
    '—',
    event.reason,
  );
});

// ----------------------------------------
// Cross-tab sign-out detection
// When another tab signs out, the Firestore client in this tab becomes
// terminated. Reload to get a fresh connection.
// ----------------------------------------
window.addEventListener('storage', (e) => {
  if (e.key === 'portal_signed_out' && e.newValue) {
    window.location.replace('/');
  }
});

// On tab focus, (1) flush any writes that were queued while the tab was
// backgrounded and (2) detect a terminated Firestore client.
//
// With offline persistence enabled, update()/set() promises resolve as soon as
// the write hits the LOCAL cache — not when it reaches the server. If the
// realtime WebChannel is stalled (very common after a mobile tab is
// backgrounded/throttled, or after long inactivity), those writes sit queued
// locally and don't sync until the connection revives, which previously
// required a manual refresh ("I have to refresh for saves to work"). Toggling
// disableNetwork()->enableNetwork() tears down and re-establishes the channel,
// forcing the queued mutations to flush; listeners transparently serve from
// cache in the meantime.
//
// A terminated client (a multi-tab persistence hazard) makes these calls throw
// "client has already been terminated" — in that case reload the CURRENT page
// to get a fresh client. Never navigate to '/', which would boot the user to
// the landing/login page and look like a random sign-out (auth is still valid).
let __fsFocusKickInFlight = false;
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  if (__fsFocusKickInFlight) return;

  let db;
  try {
    db = firebase?.firestore?.();
  } catch (_) {
    window.location.reload();
    return;
  }
  if (!db) return;

  __fsFocusKickInFlight = true;
  Promise.resolve()
    .then(() => db.disableNetwork())
    .then(() => db.enableNetwork())
    .then(() => db.waitForPendingWrites())
    .catch((err) => {
      if (
        String(err?.message || '').includes(
          'client has already been terminated',
        )
      ) {
        window.location.reload();
      }
    })
    .finally(() => {
      __fsFocusKickInFlight = false;
    });
});

// ----------------------------------------
// Store + React-Redux-Firebase setup
// ----------------------------------------
const store = configureStore();

const rrfConfig = {
  userProfile: 'users',
  useFirestoreForProfile: true,
};

const rrfProps = {
  firebase,
  config: rrfConfig,
  dispatch: store.dispatch,
  createFirestoreInstance,
};

// ----------------------------------------
// Mount
// ----------------------------------------
const rootEl = document.getElementById('root');
const root = createRoot(rootEl);

root.render(
  <Provider store={store}>
    <ReactReduxFirebaseProvider {...rrfProps}>
      <BrowserRouter>
        <ScrollToTop>
          <ReduxToastr
            timeOut={4000}
            position="bottom-right"
            transitionIn="fadeIn"
            transitionOut="fadeOut"
            progressBar
          />
          <App />
          <InstallPrompt appName="React Portal" />
        </ScrollToTop>
      </BrowserRouter>
    </ReactReduxFirebaseProvider>
  </Provider>,
);

// Handle iOS service worker recovery on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', handleIOSServiceWorkerRecovery);
} else {
  handleIOSServiceWorkerRecovery();
}

// ----------------------------------------
// Enhanced Service Worker registration + update UI
// ----------------------------------------
// Store current version so we can detect if the running code is already up-to-date
const currentVersionStr =
  typeof versionDate === 'string' ? versionDate : versionDate.toString();
localStorage.setItem('portal_running_version', currentVersionStr);

// Capture whether this page loaded while an SW was already controlling it.
// On a first visit there's no controller yet, so the initial clientsClaim()
// fires `controllerchange` — we must NOT reload in that case. Only tabs that
// were already controlled by the *old* SW should auto-refresh when the new SW
// takes over (see the controllerchange listener below).
const __hadControllerAtLoad =
  'serviceWorker' in navigator && !!navigator.serviceWorker.controller;

// Persistent safeguard for the multi-tab case: when a user clicks "Reload" in
// one tab, that tab's new SW calls skipWaiting()+clientsClaim(), which claims
// EVERY open tab. Without this, background tabs keep running the old bundle
// (risking a ChunkLoadError when they lazy-load an old chunk that no longer
// exists after deploy). Here we reload those background tabs exactly once, on
// the same reload path that suppresses the re-prompt. Guarded against loops
// and against the first-install claim above.
let __swReloading = false;
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (__swReloading) return;
    if (!__hadControllerAtLoad) return; // first install — don't reload
    __swReloading = true;
    performReload();
  });
}

// ----------------------------------------
// Safe auto-update (no button)
// When a new version installs, we apply it automatically — but only at a moment
// that won't interrupt the user: on an SPA route change or when they return to
// the tab, and never while they're actively typing into a field. This keeps
// everyone on the latest version without a "Reload" button and without risking
// unsaved data in the long case-management forms.
// ----------------------------------------
let __pendingUpdateReg = null;

function __isSafeToUpdateNow() {
  // Never interrupt active text entry.
  const el = document.activeElement;
  if (el) {
    const tag = el.tagName;
    if (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      el.isContentEditable
    ) {
      return false;
    }
  }
  return true;
}

function __applyPendingUpdateIfSafe() {
  if (!__pendingUpdateReg) return;
  if (!__isSafeToUpdateNow()) return;
  const reg = __pendingUpdateReg;
  __pendingUpdateReg = null;
  handleServiceWorkerUpdate(reg);
}

// Emit a 'locationchange' event on SPA navigations (react-router drives history
// via pushState/replaceState) so we can update at a natural page boundary.
(function patchHistoryForSafeUpdates() {
  const emit = () => window.dispatchEvent(new Event('locationchange'));
  const origPush = window.history.pushState;
  window.history.pushState = function (...args) {
    const ret = origPush.apply(this, args);
    emit();
    return ret;
  };
  const origReplace = window.history.replaceState;
  window.history.replaceState = function (...args) {
    const ret = origReplace.apply(this, args);
    emit();
    return ret;
  };
  window.addEventListener('popstate', emit);
})();

window.addEventListener('locationchange', __applyPendingUpdateIfSafe);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') __applyPendingUpdateIfSafe();
});

serviceWorker.register({
  onSuccess: () => {
    // Optional: show a toast here if you want
    // toastr.success('Ready for offline use');
  },
  onUpdate: (registration) => {
    // Skip the popup if the app just reloaded for a SW update
    const justUpdated = sessionStorage.getItem('sw_just_updated');
    if (justUpdated) {
      const elapsed = Date.now() - parseInt(justUpdated, 10);
      sessionStorage.removeItem('sw_just_updated');
      // If we reloaded within the last 30 seconds, the user is already on the new version
      if (elapsed < 30000) return;
    }

    // NOTE: We intentionally do NOT gate on a version comparison here.
    // `currentVersionStr` is baked into the *currently running* bundle, and
    // `portal_running_version` is written from that same value on load — so the
    // two are always equal and the old guard silently swallowed every update
    // (skip-waiting without reloading), leaving open tabs stuck on the old
    // bundle after a deploy. The `onUpdate` event is itself the reliable signal
    // that a new SW with new assets has installed. The `sw_just_updated` check
    // above still prevents reload loops.
    //
    // Safe auto-update (no button): queue the update and apply it at the next
    // safe moment — an SPA route change or when the tab regains focus — unless
    // the user is actively typing. We intentionally do NOT apply it immediately
    // here, to avoid a jarring reload right after the page loads. This keeps
    // everyone current without a prompt and without interrupting form entry.
    __pendingUpdateReg = registration;
  },
});
